'use client';

// The chrome around a mini-game board (see miniGame.ts for the play logic): title, date, the grid
// (passed as children), the How-to-Play link, the Undo/Hint/Check + highlight controls, the solved
// state, and the message list. Game-agnostic — it renders whatever useMiniGame produces plus the
// grid the caller hands in.

import React, { FC, ReactNode, useState } from 'react';
import { BoardMessage, BoardMessageType, MiniGameControls } from '@puzzlio/core';
import { useUndoShortcut } from './useUndoShortcut';
import { BodyHeading } from './typography';
import { GameButtons, HighlightButtons } from './GameButtons';
import { SolvedTextSVG } from './SolvedTextSVG';
import { useHighlight } from './HighlightProvider';

/** Format a YYYY-MM-DD string as e.g. "July 9, 2026", parsed as local time to avoid UTC drift. */
export function formatMiniGameDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

const messageColors: Record<BoardMessageType, string> = {
  [BoardMessageType.success]: 'border-green-700 bg-green-100 text-green-800',
  [BoardMessageType.error]: 'border-coral-700 bg-coral-100 text-coral-800',
  [BoardMessageType.hint]: 'border-yellow-700 bg-yellow-100 text-yellow-800',
  [BoardMessageType.tutorial]: 'border-blue-700 bg-blue-100 text-blue-800',
};

const MiniMessageBox: FC<{ message: BoardMessage }> = ({ message }) => (
  <div
    className={`rounded-lg border-2 p-3 text-sm font-semibold shadow-[3px_3px_0px_0px] ${messageColors[message.type]}`}
  >
    {message.message}
  </div>
);

export const MiniGameShell: FC<{
  date: string;
  gameName: ReactNode;
  game: MiniGameControls;
  children: ReactNode;
  /** Render the game's How-to-Play modal; receives an onClose bound to the shell's own open state. */
  howToPlay?: (onClose: () => void) => ReactNode;
}> = ({ date, gameName, game, children, howToPlay }) => {
  const [howToPlayOpen, setHowToPlayOpen] = useState(false);
  const { mode: highlightMode, configs: highlightConfigs, setMode: setHighlightMode } = useHighlight();
  const { complete, messages, pressedButton, hasHint, handleUndo, handleHint, handleCheck } = game;

  // Gated on !complete to match the buttons below, which a solved board replaces with the solved
  // state — so the shortcut can't reach an undo the shell no longer offers.
  useUndoShortcut(handleUndo, !complete);

  return (
    <div className="flex w-full max-w-sm flex-col items-center gap-4">
      <div className="flex flex-col items-center gap-1">
        {gameName}
        <BodyHeading>{formatMiniGameDate(date)}</BodyHeading>
      </div>

      {children}

      {!complete && (
        <button
          onClick={() => setHowToPlayOpen(true)}
          className="text-sm font-semibold text-black/50 underline underline-offset-2 hover:text-black/70"
        >
          How to Play
        </button>
      )}

      {howToPlayOpen && howToPlay?.(() => setHowToPlayOpen(false))}

      {complete ? (
        <div className="flex w-full flex-col items-center gap-3">
          <SolvedTextSVG className="w-full" />
          <p className="text-center text-sm text-black/60">
            To track your solve times, replay puzzles, and access the archive, visit{' '}
            <a
              href="https://inkwellgames.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-black/80 underline underline-offset-2"
            >
              Inkwell Games
            </a>
            .
          </p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <GameButtons
            onUndoClicked={handleUndo}
            onHintClicked={hasHint ? handleHint : undefined}
            onCheckClicked={handleCheck}
            pressedButton={pressedButton}
            className="mx-auto"
          />
          <HighlightButtons
            highlightMode={highlightMode}
            highlightConfigs={highlightConfigs}
            setHighlightMode={setHighlightMode}
          />
        </div>
      )}

      <div className={`flex min-h-[88px] w-full flex-col gap-2 ${messages.length === 0 ? 'invisible' : ''}`}>
        {messages.map((msg, i) => (
          <MiniMessageBox key={i} message={msg} />
        ))}
      </div>
    </div>
  );
};
